// src/pages/Reports.js
import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  CalendarDays, Table2, CreditCard, History,
  Download, FileSpreadsheet, FileText, UtensilsCrossed, Users
} from 'lucide-react';

const COLORS = ['#2563eb','#16a34a','#d97706','#dc2626','#7c3aed'];

const tabs = [
  { key: 'daily',       label: 'Daily Revenue',  icon: CalendarDays    },
  { key: 'table',       label: 'Table Usage',    icon: Table2          },
  { key: 'payment',     label: 'Payment Mode',   icon: CreditCard      },
  { key: 'history',     label: 'Session History',icon: History         },
  { key: 'customer',    label: 'Customer Bills', icon: Users           },
  { key: 'food_profit', label: 'Food Profit',    icon: UtensilsCrossed },
];

function today()      { return new Date().toISOString().slice(0,10); }
function monthStart() { return new Date().toISOString().slice(0,8) + '01'; }

// ── CSV export ───────────────────────────────────────────────
function exportCSV(data, filename, columns) {
  const header = columns.map(c => c.label).join(',');
  const rows   = data.map(row =>
    columns.map(c => {
      const val = row[c.key] ?? '';
      return typeof val === 'string' && val.includes(',') ? `"${val}"` : val;
    }).join(',')
  );
  const blob = new Blob([header + '\n' + rows.join('\n')], { type:'text/csv' });
  dlBlob(blob, filename + '.csv');
}

// ── Excel export ─────────────────────────────────────────────
async function exportExcel(data, filename, columns) {
  if (!window.XLSX) {
    await new Promise((res, rej) => {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
      s.onload = res; s.onerror = rej;
      document.head.appendChild(s);
    });
  }
  const XLSX    = window.XLSX;
  const headers = columns.map(c => c.label);
  const rows    = data.map(row => columns.map(c => row[c.key] ?? ''));
  const ws      = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const wb      = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Report');
  XLSX.writeFile(wb, filename + '.xlsx');
}

// ── PDF / Print ──────────────────────────────────────────────
function exportPDF(data, filename, columns, title, from, to) {
  const printWin = window.open('', '_blank');
  printWin.document.write(`
    <html><head><title>${title}</title>
    <style>
      body{font-family:Arial,sans-serif;font-size:13px;padding:24px}
      h2{color:#1e3a5f;margin-bottom:4px}
      .meta{color:#666;font-size:12px;margin-bottom:20px}
      table{border-collapse:collapse;width:100%}
      th{background:#1e3a5f;color:#fff;padding:8px 12px;text-align:left;font-size:12px}
      td{padding:7px 12px;border-bottom:1px solid #e5e7eb;font-size:12px}
      tr:nth-child(even) td{background:#f8fafc}
      @media print{button{display:none}}
    </style></head><body>
    <h2>${title}</h2>
    <div class="meta">Period: ${from} to ${to} &nbsp;|&nbsp; Generated: ${new Date().toLocaleString('en-IN')}</div>
    <table>
      <thead><tr>${columns.map(c=>`<th>${c.label}</th>`).join('')}</tr></thead>
      <tbody>${data.map(row=>`<tr>${columns.map(c=>`<td>${row[c.key]??''}</td>`).join('')}</tr>`).join('')}</tbody>
    </table>
    <br/><button onclick="window.print()">🖨️ Print / Save as PDF</button>
    </body></html>
  `);
  printWin.document.close();
}

function dlBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a   = Object.assign(document.createElement('a'), { href: url, download: filename });
  a.click();
  URL.revokeObjectURL(url);
}

// ── Column definitions ───────────────────────────────────────
const COLUMNS = {
  daily: [
    { key:'date',          label:'Date' },
    { key:'sessions',      label:'Sessions' },
    { key:'play_revenue',  label:'Play Revenue (₹)' },
    { key:'food_revenue',  label:'Food Revenue (₹)' },
    { key:'total_revenue', label:'Total Revenue (₹)' },
  ],
  table: [
    { key:'table_name',     label:'Table' },
    { key:'total_sessions', label:'Sessions' },
    { key:'total_seconds',  label:'Play Seconds' },
    { key:'play_revenue',   label:'Play Revenue (₹)' },
    { key:'food_revenue',   label:'Food Revenue (₹)' },
    { key:'total_revenue',  label:'Total Revenue (₹)' },
  ],
  payment: [
    { key:'payment_mode', label:'Payment Mode' },
    { key:'count',        label:'Transactions' },
    { key:'total',        label:'Amount (₹)' },
  ],
  history: [
    { key:'id',                 label:'Session ID' },
    { key:'table_name',         label:'Table' },
    { key:'guest_name',         label:'Players' },
    { key:'total_play_seconds', label:'Play Seconds' },
    { key:'play_amount',        label:'Play (₹)' },
    { key:'food_amount',        label:'Food (₹)' },
    { key:'total_amount',       label:'Total (₹)' },
    { key:'ended_at',           label:'Date/Time' },
  ],
  customer: [
    { key:'customer_name',  label:'Customer' },
    { key:'mobile',         label:'Mobile' },
    { key:'total_sessions', label:'Sessions' },
    { key:'play_amount',    label:'Play (₹)' },
    { key:'food_amount',    label:'Food (₹)' },
    { key:'total_discount', label:'Discount (₹)' },
    { key:'total_paid',     label:'Total Paid (₹)' },
    { key:'last_session',   label:'Last Session' },
  ],
  food_profit: [
    { key:'item_name',     label:'Item' },
    { key:'total_qty',     label:'Qty Sold' },
    { key:'buying_price',  label:'Buy Price (₹)' },
    { key:'selling_price', label:'Sell Price (₹)' },
    { key:'total_cost',    label:'Total Cost (₹)' },
    { key:'total_revenue', label:'Revenue (₹)' },
    { key:'total_profit',  label:'Profit (₹)' },
    { key:'profit_pct',    label:'Margin %' },
  ],
};

const TAB_TITLES = {
  daily:       'Daily Revenue Report',
  table:       'Table Usage Report',
  payment:     'Payment Mode Report',
  history:     'Session History Report',
  customer:    'Customer Bill Report',
  food_profit: 'Food Profit Report',
};

export default function Reports() {
  const [tab,     setTab]     = useState('daily');
  const [from,    setFrom]    = useState(monthStart());
  const [to,      setTo]      = useState(today());
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reports?type=${tab}&from=${from}&to=${to}`);
      setData(Array.isArray(res.data) ? res.data : []);
    } catch (_) { setData([]); }
    setLoading(false);
  }, [tab, from, to]);

  useEffect(() => { load(); }, [load]);

  const fmt     = n => '₹' + Number(n||0).toLocaleString('en-IN', { minimumFractionDigits:2 });
  const fmtSecs = s => { const h=Math.floor(s/3600), m=Math.floor((s%3600)/60); return `${h}h ${m}m`; };
  const totalRevenue = data.reduce((a,r) => a + parseFloat(r.total_revenue || r.total || 0), 0);

  const filename = `Snooker_${TAB_TITLES[tab].replace(/ /g,'_')}_${from}_to_${to}`;
  const doCSV   = () => exportCSV(data, filename, COLUMNS[tab]);
  const doExcel = () => exportExcel(data, filename, COLUMNS[tab]);
  const doPDF   = () => exportPDF(data, filename, COLUMNS[tab], TAB_TITLES[tab], from, to);

  return (
    <div>
      <div className="page-header" style={{ flexWrap:'wrap', gap:12 }}>
        <h2 className="page-title">Reports</h2>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
          <div className="date-range">
            <input type="date" value={from} onChange={e => setFrom(e.target.value)}/>
            <span>to</span>
            <input type="date" value={to}   onChange={e => setTo(e.target.value)}/>
          </div>
          {data.length > 0 && (
            <div className="download-btns">
              <button className="btn btn-outline btn-sm download-btn" onClick={doCSV}>
                <Download size={14}/> CSV
              </button>
              <button className="btn btn-success btn-sm download-btn" onClick={doExcel}>
                <FileSpreadsheet size={14}/> Excel
              </button>
              <button className="btn btn-primary btn-sm download-btn" onClick={doPDF}>
                <FileText size={14}/> PDF
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-bar">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button key={key} className={`tab-btn ${tab===key?'active':''}`} onClick={() => setTab(key)}>
            <Icon size={15}/> {label}
          </button>
        ))}
      </div>

      {loading ? <div className="loading-screen"><div className="spinner"/></div> : (
        <>
          {/* Summary banner */}
          {!['history','customer','food_profit'].includes(tab) && data.length > 0 && (
            <div className="report-summary">
              <div className="report-summary-item">
                <span>Total Revenue</span><strong>{fmt(totalRevenue)}</strong>
              </div>
              <div className="report-summary-item">
                <span>Records</span><strong>{data.length}</strong>
              </div>
            </div>
          )}

          {/* ── Daily Revenue ── */}
          {tab === 'daily' && (
            <div className="card">
              <div className="card-header">Daily Revenue Breakdown</div>
              {data.length === 0 ? <div className="empty-state">No data for selected range</div> : (
                <>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={[...data].reverse()} margin={{ top:8, right:16, left:0, bottom:0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                      <XAxis dataKey="date" tick={{ fontSize:11 }} tickFormatter={d => d?.slice(5)}/>
                      <YAxis tick={{ fontSize:11 }} tickFormatter={v => '₹'+(v/1000).toFixed(0)+'k'}/>
                      <Tooltip formatter={v => ['₹'+Number(v).toLocaleString('en-IN')]}/>
                      <Legend/>
                      <Bar dataKey="play_revenue" name="Play" fill="#2563eb" radius={[3,3,0,0]} stackId="a"/>
                      <Bar dataKey="food_revenue" name="Food" fill="#16a34a" radius={[3,3,0,0]} stackId="a"/>
                    </BarChart>
                  </ResponsiveContainer>
                  <table className="data-table" style={{ marginTop:16 }}>
                    <thead><tr><th>Date</th><th>Sessions</th><th>Play</th><th>Food</th><th>Total</th></tr></thead>
                    <tbody>
                      {data.map(r => (
                        <tr key={r.date}>
                          <td>{r.date}</td><td>{r.sessions}</td>
                          <td>{fmt(r.play_revenue)}</td><td>{fmt(r.food_revenue)}</td>
                          <td><strong>{fmt(r.total_revenue)}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:'#fef9c3' }}>
                        <td colSpan={4}><strong>Grand Total</strong></td>
                        <td><strong>{fmt(totalRevenue)}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </>
              )}
            </div>
          )}

          {/* ── Table Usage ── */}
          {tab === 'table' && (
            <div className="card">
              <div className="card-header">Table Performance</div>
              {data.length === 0 ? <div className="empty-state">No data for selected range</div> : (
                <>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={data} margin={{ top:8, right:16, left:0, bottom:0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                      <XAxis dataKey="table_name" tick={{ fontSize:11 }}/>
                      <YAxis tick={{ fontSize:11 }} tickFormatter={v => '₹'+(v/1000).toFixed(0)+'k'}/>
                      <Tooltip formatter={v => ['₹'+Number(v).toLocaleString('en-IN')]}/>
                      <Bar dataKey="total_revenue" name="Revenue" fill="#7c3aed" radius={[4,4,0,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                  <table className="data-table" style={{ marginTop:16 }}>
                    <thead><tr><th>Table</th><th>Sessions</th><th>Play Time</th><th>Play</th><th>Food</th><th>Total</th></tr></thead>
                    <tbody>
                      {data.map(r => (
                        <tr key={r.table_name}>
                          <td><strong>{r.table_name}</strong></td>
                          <td>{r.total_sessions}</td>
                          <td>{fmtSecs(r.total_seconds)}</td>
                          <td>{fmt(r.play_revenue)}</td>
                          <td>{fmt(r.food_revenue)}</td>
                          <td><strong>{fmt(r.total_revenue)}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}

          {/* ── Payment Mode ── */}
          {tab === 'payment' && (
            <div className="card">
              <div className="card-header">Payment Mode Summary</div>
              {data.length === 0 ? <div className="empty-state">No data for selected range</div> : (
                <div style={{ display:'flex', gap:24, flexWrap:'wrap', alignItems:'center' }}>
                  <ResponsiveContainer width={260} height={220}>
                    <PieChart>
                      <Pie data={data} dataKey="total" nameKey="payment_mode" cx="50%" cy="50%" outerRadius={90}
                        label={({ payment_mode, percent }) => `${payment_mode} ${(percent*100).toFixed(0)}%`}>
                        {data.map((_,i) => <Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                      </Pie>
                      <Tooltip formatter={v => ['₹'+Number(v).toLocaleString('en-IN')]}/>
                    </PieChart>
                  </ResponsiveContainer>
                  <table className="data-table" style={{ flex:1, minWidth:240 }}>
                    <thead><tr><th>Mode</th><th>Transactions</th><th>Amount</th></tr></thead>
                    <tbody>
                      {data.map((r,i) => (
                        <tr key={r.payment_mode}>
                          <td>
                            <span style={{ background:COLORS[i%COLORS.length], color:'#fff', padding:'2px 10px', borderRadius:12, textTransform:'capitalize', fontSize:12 }}>
                              {r.payment_mode}
                            </span>
                          </td>
                          <td>{r.count}</td>
                          <td><strong>{fmt(r.total)}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:'#fef9c3' }}>
                        <td><strong>Total</strong></td>
                        <td><strong>{data.reduce((a,r)=>a+parseInt(r.count),0)}</strong></td>
                        <td><strong>{fmt(totalRevenue)}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Session History ── */}
          {tab === 'history' && (
            <div className="card">
              <div className="card-header">Session History ({data.length} sessions)</div>
              {data.length === 0 ? <div className="empty-state">No sessions for selected range</div> : (
                <div style={{ overflowX:'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr><th>#</th><th>Table</th><th>Players</th><th>Play Time</th><th>Play</th><th>Food</th><th>Total</th><th>Date</th></tr>
                    </thead>
                    <tbody>
                      {data.map(r => (
                        <tr key={r.id}>
                          <td>{r.id}</td>
                          <td>{r.table_name}</td>
                          <td>{r.guest_name}</td>
                          <td>{fmtSecs(r.total_play_seconds)}</td>
                          <td>{fmt(r.play_amount)}</td>
                          <td>{fmt(r.food_amount)}</td>
                          <td><strong>{fmt(r.total_amount)}</strong></td>
                          <td style={{ fontSize:12, color:'#666' }}>
                            {r.ended_at ? new Date(r.ended_at).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:'#fef9c3' }}>
                        <td colSpan={6}><strong>Grand Total</strong></td>
                        <td><strong>{fmt(data.reduce((a,r)=>a+parseFloat(r.total_amount||0),0))}</strong></td>
                        <td/>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Customer Bills ── */}
          {tab === 'customer' && (
            <div className="card">
              <div className="card-header">Customer Bill Summary — {data.length} customers</div>
              {data.length === 0 ? <div className="empty-state">No customer data for selected range</div> : (
                <div style={{ overflowX:'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr><th>Customer</th><th>Mobile</th><th>Sessions</th><th>Play</th><th>Food</th><th>Discount</th><th>Total Paid</th><th>Last Session</th></tr>
                    </thead>
                    <tbody>
                      {data.map(r => (
                        <tr key={r.customer_id}>
                          <td><strong>{r.customer_name}</strong></td>
                          <td style={{ fontSize:12, color:'#64748b' }}>{r.mobile}</td>
                          <td>{r.total_sessions}</td>
                          <td>{fmt(r.play_amount)}</td>
                          <td>{fmt(r.food_amount)}</td>
                          <td style={{ color:'#22c55e', fontWeight:600 }}>
                            {parseFloat(r.total_discount||0) > 0 ? '-'+fmt(r.total_discount) : '—'}
                          </td>
                          <td><strong>{fmt(r.total_paid)}</strong></td>
                          <td style={{ fontSize:12, color:'#94a3b8' }}>
                            {r.last_session ? new Date(r.last_session).toLocaleDateString('en-IN') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:'#fef9c3', fontWeight:700 }}>
                        <td colSpan={3}>Grand Total</td>
                        <td>{fmt(data.reduce((a,r)=>a+parseFloat(r.play_amount||0),0))}</td>
                        <td>{fmt(data.reduce((a,r)=>a+parseFloat(r.food_amount||0),0))}</td>
                        <td style={{ color:'#22c55e' }}>-{fmt(data.reduce((a,r)=>a+parseFloat(r.total_discount||0),0))}</td>
                        <td>{fmt(data.reduce((a,r)=>a+parseFloat(r.total_paid||0),0))}</td>
                        <td/>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Food Profit ── */}
          {tab === 'food_profit' && (
            <div className="card">
              <div className="card-header">Food Profit Analysis</div>
              {data.length === 0 ? <div className="empty-state">No food orders for selected range</div> : (
                <>
                  <div className="inv-stats-row" style={{ marginBottom:16 }}>
                    <div className="inv-stat">
                      <div className="inv-stat-value">{data.reduce((a,r)=>a+parseInt(r.total_qty||0),0)}</div>
                      <div className="inv-stat-label">Items Sold</div>
                    </div>
                    <div className="inv-stat">
                      <div className="inv-stat-value" style={{ color:'#ef4444' }}>{fmt(data.reduce((a,r)=>a+parseFloat(r.total_cost||0),0))}</div>
                      <div className="inv-stat-label">Total Cost</div>
                    </div>
                    <div className="inv-stat">
                      <div className="inv-stat-value" style={{ color:'#2563eb' }}>{fmt(data.reduce((a,r)=>a+parseFloat(r.total_revenue||0),0))}</div>
                      <div className="inv-stat-label">Revenue</div>
                    </div>
                    <div className="inv-stat">
                      <div className="inv-stat-value" style={{ color:'#22c55e' }}>{fmt(data.reduce((a,r)=>a+parseFloat(r.total_profit||0),0))}</div>
                      <div className="inv-stat-label">Net Profit</div>
                    </div>
                  </div>
                  <table className="data-table">
                    <thead>
                      <tr><th>Item</th><th>Qty</th><th>Buy Price</th><th>Sell Price</th><th>Cost</th><th>Revenue</th><th>Profit</th><th>Margin</th></tr>
                    </thead>
                    <tbody>
                      {data.map(r => (
                        <tr key={r.item_name}>
                          <td><strong>{r.item_name}</strong></td>
                          <td>{r.total_qty}</td>
                          <td>₹{Number(r.buying_price||0).toFixed(2)}</td>
                          <td>₹{Number(r.selling_price||0).toFixed(2)}</td>
                          <td style={{ color:'#ef4444' }}>{fmt(r.total_cost)}</td>
                          <td style={{ color:'#2563eb' }}>{fmt(r.total_revenue)}</td>
                          <td style={{ fontWeight:700, color: parseFloat(r.total_profit)>=0?'#22c55e':'#ef4444' }}>{fmt(r.total_profit)}</td>
                          <td>
                            <span style={{
                              background: parseFloat(r.profit_pct)>=30?'#dcfce7':parseFloat(r.profit_pct)>=10?'#fef9c3':'#fee2e2',
                              color:      parseFloat(r.profit_pct)>=30?'#15803d':parseFloat(r.profit_pct)>=10?'#92400e':'#b91c1c',
                              padding:'2px 8px', borderRadius:12, fontSize:12, fontWeight:600
                            }}>
                              {r.profit_pct}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:'#fef9c3', fontWeight:700 }}>
                        <td>Total</td>
                        <td>{data.reduce((a,r)=>a+parseInt(r.total_qty||0),0)}</td>
                        <td colSpan={2}/>
                        <td style={{ color:'#ef4444' }}>{fmt(data.reduce((a,r)=>a+parseFloat(r.total_cost||0),0))}</td>
                        <td style={{ color:'#2563eb' }}>{fmt(data.reduce((a,r)=>a+parseFloat(r.total_revenue||0),0))}</td>
                        <td style={{ color:'#22c55e' }}>{fmt(data.reduce((a,r)=>a+parseFloat(r.total_profit||0),0))}</td>
                        <td/>
                      </tr>
                    </tfoot>
                  </table>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
